import axios from 'axios';
import ReconnectingWebSocket from 'reconnecting-websocket';

import { IOrderbook, ITickerSocket, TradeSocket } from 'types/cex/spot';
import { cexExchangesTimeframes, IChartTimeframe, KLine } from 'types/charts';
import { normalizeNumber } from 'utils/formats/normalizeNumber';
import { ECexName } from 'web3';

import { BaseStrategy, IInitArgs } from '../base_strategy';
import { aggregateOrderbook, mergeOrderBook, sortOrderbookFunc } from '../utils';

export class BitgetStrategy implements BaseStrategy {
  ws: ReconnectingWebSocket | undefined;
  depthWs: ReconnectingWebSocket | undefined;
  pingInterval: ReturnType<typeof setInterval> | undefined;

  bids: [string, string][];
  asks: [string, string][];
  pair: string;
  depth: number;
  precision: number;
  onOrderbookUpdate: (v: IOrderbook) => void;
  onTickerUpdate: (v: ITickerSocket) => void;
  klineUpdate: (v: KLine) => void;
  precisionUpdate: (v: number) => void;
  onTradeUpdate: (v: TradeSocket) => void;

  symbolInternalName: string;
  selectedPrecision: number;

  constructor({
    base,
    quote,
    orderbookDepth,
    onOrderbookUpdate,
    onTickerUpdate,
    klineUpdate,
    precisionUpdate,
    onTradeUpdate,
  }: IInitArgs) {
    this.pair = `${base.toUpperCase()}${quote.toUpperCase()}`;
    this.bids = [];
    this.asks = [];
    this.depth = orderbookDepth ?? 20;
    this.precision = 1;
    this.selectedPrecision = 1;
    this.symbolInternalName = '';
    this.onOrderbookUpdate = onOrderbookUpdate;
    this.onTickerUpdate = onTickerUpdate;
    this.klineUpdate = klineUpdate;
    this.precisionUpdate = precisionUpdate;
    this.onTradeUpdate = onTradeUpdate;
  }

  fetchTickerPrecision = async () => {
    try {
      const res = await axios.get<{ data: { pricePrecision: number }[] }>(
        `https://api.bitget.com/api/v2/spot/public/symbols?symbol=${this.pair}`,
      );

      let prec = 1;

      if (res.data) {
        prec = res.data.data[0]?.pricePrecision ?? 1;
      }

      this.precision = prec;
      this.selectedPrecision = prec;
      this.precisionUpdate(prec);
    } catch (error) {
      console.log('Bitget ticker error: ', error);
    }
  };

  fetchInternalSymbolName = async () => {
    try {
      const res = await axios.get<{ data: { symbol: string }[] }>(
        'https://api.bitget.com/api/v3/market/instruments?category=SPOT',
      );

      if (res.data) {
        const find = res.data.data.find(el => el.symbol === this.pair);

        if (find) this.symbolInternalName = find.symbol;
      }
    } catch (error) {
      console.log('Bitget symbol name error: ', error);
    }
  };

  fetchKlines = async ({
    base,
    quote,
    limit,
    interval,
  }: {
    base: string;
    quote: string;
    limit: number;
    interval: IChartTimeframe;
  }) => {
    const innerInterval = cexExchangesTimeframes[interval][ECexName.bitget];

    try {
      const result = await axios.get<{
        data?: string[];
      }>('https://api.bitget.com/api/v2/spot/market/candles', {
        params: {
          symbol: `${base}${quote}`,
          limit,
          granularity: innerInterval,
        },
      });

      return (result.data.data ?? []).map(el => [
        Number(el[0]),
        el[1],
        el[2],
        el[3],
        el[4],
        el[5],
      ]) as KLine[];
    } catch (error) {
      console.log('Bitget klines error: ', error);

      return [];
    }
  };

  fetchTrades = async ({ base, quote, limit }: { base: string; quote: string; limit: number }) => {
    try {
      const result = await axios.get<{
        data?: {
          price: string;
          size: string;
          ts: string;
          side: string;
        }[];
      }>('https://api.bitget.com/api/v2/spot/market/fills', {
        params: {
          symbol: `${base}${quote}`,
          limit,
        },
      });

      return (
        (result.data.data ?? []).map(item => ({
          price: item.price,
          quantity: item.size,
          timestamp: item.ts,
          side: item.side,
        })) ?? []
      );
    } catch (error) {
      console.log('Bitget trades error: ', error);
      return [];
    }
  };

  connectWebSocket = ({ interval }: { interval: number }) => {
    if (this.ws) {
      this.closeWebSocket();
    }

    const ws = new ReconnectingWebSocket('wss://ws.bitget.com/v2/ws/public', [], {
      maxRetries: 10,
      minReconnectionDelay: 1000,
    });

    const depthWs = new ReconnectingWebSocket('wss://stream.bitget.com/public/v1/stream', [], {
      maxRetries: 10,
      minReconnectionDelay: 1000,
    });

    this.ws = ws;
    this.depthWs = depthWs;

    const intervalChannel = {
      60000: 'candle1m',
      300000: 'candle5m',
      900000: 'candle15m',
      3600000: 'candle1H',
      14400000: 'candle4H',
      86400000: 'candle1D',
      604800000: 'candle1W',
    }[interval];

    this.pingInterval = setInterval(() => {
      if (this.ws) {
        this.ws.send('ping');
      }
      if (this.depthWs) {
        this.depthWs.send('ping');
      }
    }, 60 * 1000);

    this.ws.onopen = () => {
      ws.send(
        JSON.stringify({
          op: 'subscribe',
          args: [
            {
              channel: 'ticker',
              instId: this.pair,
              instType: 'SPOT',
            },
          ],
        }),
      );

      ws.send(
        JSON.stringify({
          op: 'subscribe',
          args: [
            {
              channel: intervalChannel,
              instId: this.pair,
              instType: 'SPOT',
            },
          ],
        }),
      );

      ws.send(
        JSON.stringify({
          op: 'subscribe',
          args: [
            {
              channel: 'trade',
              instId: this.pair,
              instType: 'SPOT',
            },
          ],
        }),
      );
    };

    depthWs.onopen = () => {
      depthWs.send(
        JSON.stringify({
          op: 'subscribe',
          args: [
            {
              channel: 'depth',
              instId: this.symbolInternalName,
              instType: 'sp',
              params: { scale: normalizeNumber(1 / 10 ** this.selectedPrecision) },
            },
          ],
        }),
      );
    };

    this.ws.onmessage = event => {
      try {
        // Skip non-JSON messages like "pong"
        if (event.data === 'pong') {
          return;
        }

        const data = JSON.parse(event.data);

        if (data?.arg?.channel === 'ticker' && data?.data?.[0]) {
          const { baseVolume, change24h, lastPr, high24h, low24h, quoteVolume } = data.data[0];

          this.onTickerUpdate({
            baseVolume,
            change: normalizeNumber(Number(change24h) * 100),
            close: lastPr,
            high: high24h,
            last: Number(lastPr).toFixed(this.precision),
            low: low24h,
            quoteVolume,
          });
        }

        if (data?.arg?.channel === intervalChannel && data?.data?.[0]) {
          const [time, open, high, low, close, volume] = data.data[data.data.length - 1];

          this.klineUpdate([Math.floor(Number(time) / 1000), open, high, low, close, volume]);
        }

        if (data?.arg?.channel === 'trade' && data?.data?.[0]) {
          const { ts, price, side, size } = data.data[0];

          this.onTradeUpdate({
            price,
            quantity: size,
            timestamp: ts,
            side,
          });
        }
      } catch (error) {
        console.log('Bitget socket error', error);
      }
    };

    this.depthWs.onmessage = event => {
      try {
        // Skip non-JSON messages like "pong"
        if (event.data === 'pong') {
          return;
        }

        const data = JSON.parse(event.data);

        if (data?.arg?.channel === 'depth' && data?.data?.[0]) {
          const action = data.action as 'update' | 'snapshot';

          const responseAsks = data.data[0].asks;
          const responseBids = data.data[0].bids;

          const asks =
            action === 'snapshot'
              ? mergeOrderBook(responseAsks, []).sort(sortOrderbookFunc)
              : mergeOrderBook(this.asks, responseAsks).sort(sortOrderbookFunc);

          const bids =
            action === 'snapshot'
              ? mergeOrderBook(responseBids, []).sort(sortOrderbookFunc)
              : mergeOrderBook(this.bids, responseBids).sort(sortOrderbookFunc);

          // Limit number of levels to this.depth
          this.asks = asks.slice(0, this.depth);
          this.bids = bids.slice(0, this.depth);

          this.onOrderbookUpdate(
            aggregateOrderbook(
              this.asks.map(el => [Number(el[0]).toFixed(this.precision), el[1]]),
              this.bids.map(el => [Number(el[0]).toFixed(this.precision), el[1]]),
              this.selectedPrecision,
            ),
          );
        }
      } catch (error) {
        console.log('Bitget depth socket error: ', error);
      }
    };
  };

  closeWebSocket = () => {
    if (this.ws) {
      this.ws.close();
    }
    if (this.depthWs) {
      this.depthWs.close();
    }
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
    }
  };

  switchOrderbookTick = ({ tick }: { tick: number }) => {
    if (this.depthWs) {
      this.onOrderbookUpdate(
        aggregateOrderbook(
          this.asks.map(el => [Number(el[0]).toFixed(this.precision), el[1]]),
          this.bids.map(el => [Number(el[0]).toFixed(this.precision), el[1]]),
          tick,
        ),
      );

      this.depthWs.send(
        JSON.stringify({
          op: 'unsubscribe',
          args: [
            {
              channel: 'depth',
              instId: this.symbolInternalName,
              instType: 'sp',
              params: { scale: normalizeNumber(1 / 10 ** this.selectedPrecision) },
            },
          ],
        }),
      );

      this.selectedPrecision = tick;

      this.depthWs.send(
        JSON.stringify({
          op: 'subscribe',
          args: [
            {
              channel: 'depth',
              instId: this.symbolInternalName,
              instType: 'sp',
              params: { scale: normalizeNumber(1 / 10 ** this.selectedPrecision) },
            },
          ],
        }),
      );
    }
  };

  setOrderbookDepth(depth: number) {
    this.depth = depth;

    // If depth WebSocket is active, resubscribe with new depth
    if (this.depthWs && this.depthWs.readyState === WebSocket.OPEN) {
      // Unsubscribe from current channel
      this.depthWs.send(
        JSON.stringify({
          op: 'unsubscribe',
          args: [
            {
              channel: 'depth',
              instId: this.symbolInternalName,
              instType: 'sp',
              params: { scale: normalizeNumber(1 / 10 ** this.selectedPrecision) },
            },
          ],
        }),
      );

      // Subscribe with new depth (use depth as limit)
      // Note: legacy Bitget API may not support limit parameter directly,
      // but we can filter results on client side
      this.depthWs.send(
        JSON.stringify({
          op: 'subscribe',
          args: [
            {
              channel: 'depth',
              instId: this.symbolInternalName,
              instType: 'sp',
              params: { scale: normalizeNumber(1 / 10 ** this.selectedPrecision) },
            },
          ],
        }),
      );
    }
  }

  getOrderbookTicks = (prec: number) =>
    Array.from({ length: prec })
      .fill(0)
      .map((_, idx) => idx + 1)
      .map(el => ({ value: normalizeNumber(1 / 10 ** el), tick: el, id: el }))
      .slice(-3);

  fetch = async () => {
    await Promise.all([this.fetchInternalSymbolName(), this.fetchTickerPrecision()]);
  };

  static init = async (args: IInitArgs) => {
    const instance = new BitgetStrategy(args);

    await instance.fetch();

    return instance;
  };
}
